// Messages/styles.js
// Centralized styles for Messages page components using design system

import { COLORS, LAYOUT } from '../../constants';

// Add CSS animation styles to document head if not already added
if (typeof document !== 'undefined' && !document.getElementById('messages-animations')) {
  const style = document.createElement('style');
  style.id = 'messages-animations';
  style.textContent = `
    @keyframes pulse {
      0% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
      }
      70% {
        box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
      }
      100% {
        box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
      }
    }
  `;
  document.head.appendChild(style);
}

export const styles = {
  // Page Container
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: LAYOUT.background,
    padding: '40px 20px',
  },

  // Main Container
  container: {
    maxWidth: '900px',
    margin: '0 auto',
  },

  // Header
  header: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, #a00e1f 100%)`,
    padding: '48px 40px',
    borderRadius: '16px',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: '32px',
    boxShadow: '0 4px 16px rgba(206, 17, 38, 0.2)',
  },

  iconWrapper: {
    width: '64px',
    height: '64px',
    margin: '0 auto 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },

  title: {
    fontSize: '32px',
    fontWeight: '700',
    margin: '0 0 12px 0',
    letterSpacing: '-0.5px',
  },

  subtitle: {
    fontSize: '16px',
    fontWeight: '400',
    margin: 0,
    opacity: 0.9,
  },

  // Content Area
  content: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #e5e5e5',
  },

  // Thread list styles
  threadsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },

  // Thread card styles
  threadCard: {
    backgroundColor: '#ffffff',
    border: '2px solid #e5e5e5',
    borderRadius: '12px',
    padding: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'all 0.2s ease',
  },

  threadCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    borderColor: COLORS.primary,
  },

  // Avatar styles
  avatarWrapper: {
    flexShrink: 0,
  },

  avatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: `3px solid ${COLORS.primary}`,
    backgroundColor: '#f5f5f5',
  },

  avatarPlaceholder: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#fef5f6',
    border: `3px solid ${COLORS.primary}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: COLORS.primary,
  },

  // Thread info styles
  threadInfo: {
    flex: 1,
    minWidth: 0,
  },

  threadHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },

  userName: {
    fontSize: '18px',
    fontWeight: '700',
    color: COLORS.secondary,
    letterSpacing: '-0.3px',
  },

  timestamp: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    color: '#666',
  },

  lastMessage: {
    fontSize: '15px',
    color: '#666',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '1.4',
  },

  noMessageText: {
    color: '#999',
    fontStyle: 'italic',
  },

  chevron: {
    color: '#ccc',
    flexShrink: 0,
  },

  // Loading State
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e5e5',
  },

  spinnerAnimation: {
    color: COLORS.primary,
    animation: 'spin 1.5s linear infinite',
    marginBottom: '24px',
  },

  loadingText: {
    fontSize: '16px',
    color: '#666',
    margin: 0,
  },

  // Empty state styles
  emptyStateContainer: {
    textAlign: 'center',
    padding: '80px 20px',
    color: '#666',
  },

  emptyIcon: {
    color: COLORS.primary,
    opacity: 0.5,
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'center',
  },

  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 12px 0',
  },

  emptyMessage: {
    fontSize: '16px',
    color: '#666',
    maxWidth: '400px',
    margin: '0 auto',
    lineHeight: '1.6',
  },

  // No User State
  noUserContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '80px 20px',
    textAlign: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e5e5e5',
  },

  noUserIcon: {
    color: COLORS.primary,
    opacity: 0.5,
    marginBottom: '24px',
  },

  noUserTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: COLORS.secondary,
    margin: '0 0 12px 0',
  },

  noUserMessage: {
    fontSize: '16px',
    color: '#666',
    margin: 0,
  },
};